// App.js
import { Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import Login from "./pages/Login.jsx";
import PrivateRoute from "./components/PrivateRoute.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import Navbar from "./components/NavbarElements/Navbar.jsx";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop.jsx";
import "./index.css";
import { useAuth } from "./context/AuthContext";
import useAnimeStore from "./Store/AnimeStore";
import { useShallow } from "zustand/react/shallow";

// Lazy-loaded pages (code splitting)
const Home = lazy(() => import("./pages/Home.jsx"));
const AnimePage = lazy(() => import("./components/AnimePage.jsx"));
const AnimeDetails = lazy(() => import("./components/EpisodePage/AnimeDetails.jsx"));
const Profile = lazy(() => import("./pages/Profile.jsx"));
const FullProfile = lazy(() => import("./pages/FullProfile.jsx"));
const Signup = lazy(() => import("./pages/Signup.jsx"));
const AllAnime = lazy(() => import("./pages/AllAnime.jsx"));
const AllContinueWatching = lazy(() => import("./pages/AllContineWatching.jsx"));
const Wishlist = lazy(() => import("./components/Wishlist.jsx"));
const GenrePage = lazy(() => import("./pages/GenrePage.jsx"));
const ChooseUsername = lazy(() => import("./pages/ChooseUsername.jsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.jsx"));
const DMCAPolicy = lazy(() => import("./components/DMCAPolicy.jsx"));
const TermsAndConditions = lazy(() => import("./pages/T&C.jsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.jsx"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines.jsx"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));

export default function App() {
  const { user } = useAuth();
  const location = useLocation();



  const background = location.state && location.state.background;
  const { allAnimes, setAllAnimes } = useAnimeStore(
    useShallow((state) => ({
      allAnimes: state.allAnimes,
      setAllAnimes: state.setAllAnimes,
    }))
  );

  // Global fetch removed as per request to use /api/homepage in Home.jsx
  // useEffect(() => { ... }, []);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Suspense fallback={
        <div className="flex items-center justify-center h-screen">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      }>
        <Routes location={background || location}>
          <Route path="/" element={<Home />} />
          <Route path="/anime/:titleSlug" element={<AnimeDetails />} /> {/* Updated: Details Page */}
          <Route path="/watch/:titleSlug" element={<AnimePage />} />   {/* Updated: Watch Page */}
          <Route path="/genre/:genre" element={<GenrePage />} />
          <Route path="/all-anime" element={<AllAnime />} />
          <Route path="/all-continue-watching" element={<AllContinueWatching />} />

          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/choose-username" element={<ChooseUsername />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/dmca" element={<DMCAPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/guidelines" element={<CommunityGuidelines />} />

          {/* Private Routes */}
          <Route
            path="/wishlist"
            element={
              <ProtectedRoute>
                <Wishlist />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

          {/* Full public profile by username */}
          <Route path="/user/:username" element={<FullProfile />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      {/* Show the login modal when there's a background location so Home remains visible underneath */}
      {background && (
        <Routes>
          <Route path="/login" element={<Login />} />
        </Routes>
      )}
      <BackToTop />
    </>
  );
}
